import React, { useRef, useState } from 'react';
import { Sparkles, Volume2, VolumeX, Clock, Film, Zap, Star, Music, Upload, X } from 'lucide-react';
import { LTXParams } from '../types';

export type { LTXParams };

interface LTXParamsPanelProps {
    params: LTXParams;
    onChange: (params: LTXParams) => void;
    hasInputImage: boolean;
    hasInputVideo?: boolean;
    audioFile?: File | null;
    onAudioFileChange?: (file: File | null) => void;
}

// Simplified default parameters
export const LTX_DEFAULT_PARAMS: LTXParams = {
    guidance_scale: 3.0,
    num_inference_steps: 30,
    mode: 't2v',
    negative_prompt: 'low quality, worst quality, deformations, distortion, blur',
    model: 'ltx-2-pro',
    quality_preset: 'balanced',
    duration_sec: 8,
    resolution: '1080p',
    fps: 25,
    generate_audio: true,
    enhance_prompt: true,
};

// Model definitions
const LTX_MODELS = {
    'ltx-2-fast': { name: 'Fast', maxDuration: 20, icon: Zap },
    'ltx-2-pro': { name: 'Pro', maxDuration: 10, icon: Star },
};

// Simplified LTX Panel - Veo-like interface
export const LTXParamsPanel: React.FC<LTXParamsPanelProps> = ({ params, onChange, audioFile, onAudioFileChange }) => {
    const audioInputRef = useRef<HTMLInputElement>(null);

    const handleChange = <K extends keyof LTXParams>(key: K, value: LTXParams[K]) => {
        onChange({ ...params, [key]: value });
    };

    const handleAudioUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && onAudioFileChange) {
            onAudioFileChange(file);
            // Switch to audio-to-video mode
            handleChange('mode', 'a2v');
        }
        if (audioInputRef.current) audioInputRef.current.value = '';
    };

    const handleRemoveAudio = () => {
        if (onAudioFileChange) {
            onAudioFileChange(null);
            handleChange('mode', 't2v');
        }
    };

    const isAudioMode = params.mode === 'a2v';

    const currentModel = LTX_MODELS[params.model as keyof typeof LTX_MODELS];
    const isFast = params.model === 'ltx-2-fast';
    const isLongDuration = params.duration_sec > 10;

    // Duration options based on model
    const durationOptions = isFast ? [6, 8, 10, 12, 14, 16, 18, 20] : [6, 8, 10];

    // Resolution options - Fast >10s is limited
    const canUse4K = !isFast || !isLongDuration;
    const canUse1440p = !isFast || !isLongDuration;

    // FPS options - Fast >10s limited to 25fps
    const canUse50fps = !isFast || !isLongDuration;

    return (
        <div className="p-4 bg-[#1f1f1f] rounded-xl border border-gray-700 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-indigo-400" />
                    <span className="text-sm font-semibold text-white">LTX-2 Configuration</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-indigo-900/50 text-indigo-200 rounded-full border border-indigo-700/50">
                        {currentModel?.name || 'Pro'}
                    </span>
                </div>
                <button
                    onClick={() => handleChange('generate_audio', !params.generate_audio)}
                    className={`p-1.5 rounded-lg transition-all ${params.generate_audio
                        ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/50'
                        : 'bg-gray-800 text-gray-500 border border-gray-700'
                        }`}
                    title={params.generate_audio ? 'Audio: ON' : 'Audio: OFF'}
                >
                    {params.generate_audio ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </button>
            </div>

            {/* Model Selection - Simple Toggle */}
            <div>
                <label className="text-xs text-gray-400 mb-1.5 block">Modèle</label>
                <div className="grid grid-cols-2 gap-2">
                    {Object.entries(LTX_MODELS).map(([id, model]) => {
                        const Icon = model.icon;
                        const isSelected = params.model === id;
                        return (
                            <button
                                key={id}
                                onClick={() => {
                                    handleChange('model', id as LTXParams['model']);
                                    // Reset duration if switching to Pro and duration > 10
                                    if (id === 'ltx-2-pro' && params.duration_sec > 10) {
                                        handleChange('duration_sec', 10);
                                    }
                                }}
                                className={`p-2 rounded-lg border transition-all flex items-center justify-center gap-2 ${isSelected
                                    ? 'bg-indigo-600/20 border-indigo-500 text-indigo-100'
                                    : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                <span className="text-sm font-medium">{model.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Duration */}
            <div>
                <label className="text-xs text-gray-400 mb-1.5 flex items-center gap-2">
                    <Clock className="w-3 h-3" /> Durée
                </label>
                <div className="flex flex-wrap gap-1.5">
                    {durationOptions.map((sec) => (
                        <button
                            key={sec}
                            onClick={() => handleChange('duration_sec', sec)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${params.duration_sec === sec
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700'
                                }`}
                        >
                            {sec}s
                        </button>
                    ))}
                </div>
            </div>

            {/* Resolution */}
            <div>
                <label className="text-xs text-gray-400 mb-1.5 flex items-center gap-2">
                    <Film className="w-3 h-3" /> Résolution
                </label>
                <div className="flex gap-2">
                    {['1080p', '1440p', '4k'].map((res) => {
                        const isDisabled = (res === '4k' && !canUse4K) || (res === '1440p' && !canUse1440p);
                        const isSelected = params.resolution === res;
                        return (
                            <button
                                key={res}
                                onClick={() => !isDisabled && handleChange('resolution', res as LTXParams['resolution'])}
                                disabled={isDisabled}
                                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${isSelected
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : isDisabled
                                        ? 'bg-gray-900 text-gray-600 cursor-not-allowed border border-gray-800'
                                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700'
                                    }`}
                            >
                                {res.toUpperCase()}
                            </button>
                        );
                    })}
                </div>
                {isFast && isLongDuration && (
                    <p className="text-[10px] text-amber-500 mt-1 flex items-center gap-1">
                        <span>⚠️</span> Fast &gt;10s: 1080p @25fps uniquement
                    </p>
                )}
            </div>

            {/* FPS */}
            <div>
                <label className="text-xs text-gray-400 mb-1.5 block">Images/sec</label>
                <div className="flex gap-2">
                    {[25, 50].map((fpsVal) => {
                        const isDisabled = fpsVal === 50 && !canUse50fps;
                        const isSelected = params.fps === fpsVal;
                        return (
                            <button
                                key={fpsVal}
                                onClick={() => !isDisabled && handleChange('fps', fpsVal as LTXParams['fps'])}
                                disabled={isDisabled}
                                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${isSelected
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : isDisabled
                                        ? 'bg-gray-900 text-gray-600 cursor-not-allowed border border-gray-800'
                                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700'
                                    }`}
                            >
                                {fpsVal} FPS
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Audio-to-Video Mode */}
            {onAudioFileChange && (
                <div className="border-t border-gray-700 pt-4">
                    <label className="text-xs text-gray-400 mb-1.5 flex items-center gap-2">
                        <Music className="w-3 h-3" /> Audio-to-Video
                    </label>

                    <input
                        type="file"
                        ref={audioInputRef}
                        onChange={handleAudioUpload}
                        accept="audio/*"
                        className="hidden"
                    />

                    {audioFile ? (
                        <div className="flex items-center gap-2 p-2 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                            <Music className="w-4 h-4 text-purple-400" />
                            <span className="flex-1 text-sm text-purple-200 truncate">{audioFile.name}</span>
                            <span className="text-[10px] text-purple-400 bg-purple-900/50 px-1.5 py-0.5 rounded">
                                {(audioFile.size / 1024).toFixed(1)} KB
                            </span>
                            <button
                                onClick={handleRemoveAudio}
                                className="p-1 hover:bg-purple-700/50 rounded transition-colors"
                                title="Retirer l'audio"
                            >
                                <X className="w-4 h-4 text-purple-300" />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => audioInputRef.current?.click()}
                            className="w-full p-3 border-2 border-dashed border-gray-600 hover:border-purple-500/50 rounded-lg flex items-center justify-center gap-2 text-gray-400 hover:text-purple-300 transition-all"
                        >
                            <Upload className="w-4 h-4" />
                            <span className="text-xs">Ajouter un fichier audio (mp3, wav)</span>
                        </button>
                    )}

                    {isAudioMode && (
                        <p className="text-[10px] text-purple-400 mt-1.5 flex items-center gap-1">
                            <span>🎵</span> La vidéo sera synchronisée avec l'audio
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};
